import { FC, useEffect } from "react";
import { Box, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  Button,
  LabelField,
  MarketplaceMedianLabelField,
  NumberField,
  TermsAndConditions,
  UsdIntegerInput,
} from "components";
import { getTrailingYield } from "lib/finance";
import { formatCurrencyAmount } from "lib/format";
import { parseIntegerAmount, parseNumber } from "lib/parse";
import { BaseAmountSource } from "services/listings/types";
import { ListingOffer } from "services/offers/types";
import theme from "theme";

export interface FormValues {
  offerAmount: string;
  proxyAmount: string;
}

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  onSaveSuccess: () => void;
  initialValues: FormValues;
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  ltm: number;
  listingProxyIncrementAmount: number;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  offer: ListingOffer;
  openProxyAmount: string;
}

const EditOfferForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldTouched,
  isSubmitting,
  ltm,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  listingProxyIncrementAmount,
}) => {
  useEffect(() => {
    setFieldTouched("offerAmount", true, false);
    return;
  }, [setFieldTouched]);

  const offerAmount = parseNumber(values.offerAmount, 0);
  const proxyAmount = parseNumber(values.proxyAmount, 0);

  return (
    <form onSubmit={handleSubmit}>
      <Typography component="div" gutterBottom>
        <Box fontSize={13} textAlign="center" pb={2}>
          Update your offer amounts below.
        </Box>
        <Box fontSize={13} textAlign="center">
          If there is an open seller counteroffer, editing your offer will
          automatically reject the seller’s counteroffer.
        </Box>
      </Typography>
      <Box my={2} textAlign="center">
        {!!marketplaceMedian && (
          <Box my={2}>
            <MarketplaceMedianLabelField
              marketplaceMedian={marketplaceMedian}
              marketplaceMedianMultiplier={marketplaceMedianMultiplier}
              marketplaceMedianSource={marketplaceMedianSource}
            />
          </Box>
        )}
        <Box my={2}>
          <NumberField
            type="text"
            name="offerAmount"
            fullWidth
            label="Current Offer Amount*"
            value={parseIntegerAmount(values.offerAmount)}
            helperText={
              touched.offerAmount && !!errors.offerAmount
                ? errors.offerAmount
                : `Trailing Earning Yield: ${getTrailingYield(
                    ltm,
                    offerAmount,
                  )}%`
            }
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.offerAmount && !!errors.offerAmount}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        <Box my={2}>
          <NumberField
            name="proxyAmount"
            type="text"
            fullWidth
            label="Proxy Amount"
            tooltipText={
              <>
                <Box>
                  This is the maximum amount you will offer on this listing.
                  This proxy will compete against other bidders but will not
                  interact with the listing owner’s counteroffers. (You will
                  need to manually interact with those.)
                </Box>
                <Box>
                  Your proxy will immediately outbid other investor’s offers. If
                  your offer is rejected or expires, an offer one increment
                  higher will be placed the following day. This will continue
                  until an offer is accepted or your proxy amount is reached.{" "}
                  <span
                    style={{ fontWeight: theme.typography.fontWeightMedium }}
                  >
                    Offers placed by your proxy can not be withdrawn.
                  </span>
                </Box>
              </>
            }
            placeholder=""
            value={parseIntegerAmount(values.proxyAmount)}
            helperText={
              touched.proxyAmount && !!errors.proxyAmount
                ? errors.proxyAmount
                : `Trailing Earning Yield: ${getTrailingYield(
                    ltm,
                    proxyAmount,
                  )}%`
            }
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.proxyAmount && !!errors.proxyAmount}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        {values.proxyAmount && (
          <Box my={2}>
            <LabelField
              label="Increase Increment"
              value={formatCurrencyAmount("USD", listingProxyIncrementAmount)}
              tooltipText="Your offer amount will increase by this amount."
            />
          </Box>
        )}
        <Box my={2}>
          <TermsAndConditions />
          <Box my={2}>
            <Button
              variant="contained"
              customColor="green"
              size="large"
              type="submit"
              fullWidth
              disabled={isSubmitting}
              id="send-offer"
            >
              Send Offer
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const {
      buyNowPrice,
      highestOpenBuyerOfferAmount,
      offer,
      openProxyAmount,
    } = props;
    return yup.object().shape({
      offerAmount: yup
        .string()
        .test(
          "same",
          "The new offer amount must be greater than the current offer amount unless you add or edit a proxy",
          function (amount) {
            const offerDifferent =
              amount && parseNumber(amount, 0) === Number(offer.amount)
                ? false
                : true;
            const proxyDifferent =
              parseNumber(this.parent.proxyAmount, 0) ===
              parseNumber(openProxyAmount, 0)
                ? false
                : true;
            return offerDifferent || proxyDifferent;
          },
        )
        .test(
          "greaterThanPreviousOffer",
          "You can't decrease the amount of your existing offer",
          amount => !amount || parseNumber(amount, 0) >= Number(offer.amount),
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmount",
          `The offer amount must be greater than the highest open offer of ${formatCurrencyAmount(
            "USD",
            highestOpenBuyerOfferAmount,
          )}`,
          function (amount) {
            const proxyDifferent =
              parseNumber(this.parent.proxyAmount, 0) ===
              parseNumber(openProxyAmount, 0)
                ? false
                : true;
            return (
              parseNumber(amount, 0) > highestOpenBuyerOfferAmount ||
              proxyDifferent
            );
          },
        )
        .test(
          "max",
          "The offer amount can't be greater than the list price",
          amount =>
            !amount || parseNumber(amount, 0) <= (buyNowPrice || Infinity),
        )
        .required("Required"),
      proxyAmount: yup
        .string()
        .test(
          "proxyGreaterThanListPrice",
          `The proxy amount must be less than the list price (${formatCurrencyAmount(
            "USD",
            buyNowPrice,
          )})`,
          amount =>
            !amount || parseNumber(amount, 0) < (buyNowPrice || Infinity),
        )
        .test(
          "greaterThanOfferAmount",
          "The proxy amount must be greater than the offer amount",
          function (proxyAmount) {
            return (
              !proxyAmount ||
              parseNumber(proxyAmount, 0) >
                parseNumber(this.parent.offerAmount, 0)
            );
          },
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmount",
          "The proxy amount must be greater than the highest open offer",
          proxyAmount =>
            !proxyAmount ||
            parseNumber(proxyAmount, 0) > highestOpenBuyerOfferAmount,
        ),
    });
  },

  handleSubmit: async (
    values: FormValues,
    { props, setSubmitting }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    } finally {
      setSubmitting(false);
      props.onSaveSuccess();
    }
  },
};

const EnhancedIncreaseYourOfferForm = withFormik<FormProps, FormValues>(
  formikOptions,
)(EditOfferForm);

export default EnhancedIncreaseYourOfferForm;
